import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import MessagePropType from '../definitions/MessagePropType';
import Colours from '../definitions/Colours';
import Typography from '../definitions/Typography';
import messageToString from '../functions/messageToString';
import toKebabCase from '../functions/toKebabCase';
import IconChevronUp from '../graphics/ChevronUp';
import IconChevronDown from '../graphics/ChevronDown';


function HorizontalAccordion({
    activeTabIndex,
    initialTabIndex = 0,
    tabs,

    onTabChange,

    tabPanelIdPrefix,
    tabIdPrefix,

    className,
    ...otherProps
}) {
    const intl = useIntl();

    // This MUST be an index or NULL
    const [ activeTab, setActiveTab ] = useState(activeTabIndex ?? initialTabIndex);

    const isControlled = typeof activeTabIndex !== 'undefined';

    // Update activeTab state when the controlled prop changes
    useEffect(() => {
        if (isControlled) {
            setActiveTab(activeTabIndex);
        }
    }, [ activeTabIndex ]);

    function handleTabClick(index) {
        const newTabIndex = index === activeTab
            ? null
            : index;

        if (!isControlled) {
            setActiveTab(newTabIndex);
        }

        // if the newTabIndex was null we want to pass that through to onTabChange()
        onTabChange && onTabChange(newTabIndex);
    }

    const tabsAsStrings = tabs.map(tab => ({
        title: messageToString(tab.title, intl),
        subTitle: messageToString(tab.subTitle, intl),
    }));

    return (
        <Container
            className={classNames(
                className,
                'rh-display-flex rh-position-relative rh-m-0 rh-pl-0 rh-list-style-type-none rh-overflow-hidden',
                'rh-justify-content-space-around rh-align-items-stretch',
                {
                    'showing-tab-content': activeTab !== null,
                }
            )}
            role="tablist"
            {...otherProps}
        >
            <For
                each="tab"
                of={tabsAsStrings}
                index="tabIndex"
            >
                <li
                    key={`child-${removeFormatting(tab.title)}-${tabIndex}`}
                    className={classNames(
                        'tab rh-display-flex rh-align-items-flex-start rh-px-1',
                        {
                            'active': tabIndex === activeTab,
                        }
                    )}
                    role="presentation"
                >
                    <button
                        type="button"
                        role="tab"
                        className={classNames(
                            'tab-button rh-display-flex rh-align-items-center',
                            'rh-px-0_25 rh-py-1 leading-s rh-text-decoration-none rh-border-width-0',
                            'hover--rh-fg-blueberry-dark',
                            {
                                'active': tabIndex === activeTab,
                            },
                        )}
                        onClick={() => handleTabClick(tabIndex)}

                        // Clean the ID a bit since it's based on a title, which might have
                        // ampersands or spaces in it or whatever.
                        // Data-name can safely include a wider variety of characters.
                        id={`${tabIdPrefix}_${getSafeHTMLName(toKebabCase(tab.title))}`}
                        data-name={`${tabIdPrefix}_${toKebabCase(tab.title)}`}

                        aria-controls={`${tabPanelIdPrefix}_${toKebabCase(tab.title)}`}
                        aria-selected={tabIndex === activeTab}

                        // Prevents link from opening in iframe if displayed in a widget, see loader.jsx
                        data-widget-do-not-adjust-click="true"
                    >
                        <div className="rh-display-flex rh-flex-direction-column rh-align-items-center">
                            <p className="title rh-m-0 rh-text-m">
                                {tab.title}
                            </p>
                        </div>

                        <span className="chevron-wrapper rh-text-xs rh-mx-0_5">
                            <Choose>
                                <When condition={tabIndex === activeTab}>
                                    <IconChevronUp
                                        className="chevron rh-icon-2xs"
                                    />
                                </When>

                                <Otherwise>
                                    {/* includes activeTab === null */}
                                    <IconChevronDown
                                        className="chevron rh-icon-2xs"
                                    />
                                </Otherwise>
                            </Choose>
                        </span>
                    </button>
                </li>
            </For>
        </Container>
    );
}

HorizontalAccordion.propTypes = {
    activeTabIndex: PropTypes.number,
    initialTabIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([ null ]),
    ]),
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            title: MessagePropType.isRequired,
        }),
    ),

    onTabChange: PropTypes.func,

    tabPanelIdPrefix: PropTypes.string.isRequired,
    tabIdPrefix: PropTypes.string.isRequired,

    className: PropTypes.string,
    otherProps: PropTypes.object,
};

const Container = styled.ul`
    // horizontal separator - show only when there is a active tab.
    &.showing-tab-content {
        &::after {
            content: '';
            display: block;
            width: 100%;
            height: 1px;
            position: absolute;
            left: 0;
            bottom: 0;
            background-color: ${Colours.STONE};
        }
    }

    > .tab {
        > .tab-button {
            > .chevron-wrapper {
                line-height: 0;
            }

            &.active {
                font-weight: ${Typography.WEIGHTS.MEDIUM};
                color: ${Colours.BLUEBERRY_DARK};

                .chevron {
                    stroke: ${Colours.BLUEBERRY_DARK};
                }
            }

            &:hover {
                .chevron {
                    stroke: ${Colours.BLUEBERRY_DARK};
                }
            }
        }
    }
`;

function removeFormatting(label) {
    return label.replace(/\s+/g,'').toLocaleLowerCase();
}

// @TODO: This is a duplicate of this function from web-components.
// Possibly consider moving to base-ui?
function getSafeHTMLName(string) {
    return string.replace(/[^A-Za-z0-6_\-:.]/g, '');
}

export default HorizontalAccordion;
